import { useState } from 'react'
import { TrendingUp, Receipt, IdCard, Wallet, AlertTriangle } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { DataTable } from '@/components/DataTable'
import { useTaxEstimate } from '@/features/tax/extras'
import { formatPayrollMoney } from '@/features/payroll/format'

function Row({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: 'success' | 'warning' | 'danger' }) {
  const valueClass =
    accent === 'success' ? 'text-emerald-600' :
    accent === 'warning' ? 'text-amber-600' :
    accent === 'danger' ? 'text-destructive' : 'text-foreground'
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-2 last:border-0">
      <div>
        <div className="text-sm">{label}</div>
        {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
      </div>
      <div className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value}</div>
    </div>
  )
}

export function TaxEstimatePage() {
  const now = new Date()
  const [taxYear, setTaxYear] = useState(now.getFullYear())
  const [quarter, setQuarter] = useState<number | ''>('')
  const { data, isLoading, isError, error, refetch, isFetching } = useTaxEstimate({
    tax_year: taxYear,
    quarter: quarter === '' ? null : Number(quarter),
  })
  const e = data?.data

  return (
    <Page
      title="Tax Estimate"
      description="Real tax numbers computed from your posted accounting entries, invoices, bills, vendors, and pay runs. No separate data entry - this is your actual ledger projected through the tax rules."
    >
      <Toolbar align="start">
        <FormField
          label="Year"
          inputMode="numeric"
          value={String(taxYear)}
          onChange={(ev) => setTaxYear(Number(ev.target.value.replace(/\D/g, '')) || now.getFullYear())}
          className="w-28"
        />
        <FormField
          label=""
          control={
            <select
              value={String(quarter)}
              onChange={(ev) => setQuarter(ev.target.value === '' ? '' : Number(ev.target.value))}
              className="h-9 w-32 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Annual</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
            </select>
          }
        />
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Toolbar>

      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : isError || !e ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-base font-semibold text-destructive">Couldn&apos;t compute estimate</h2>
            <p className="text-sm text-muted-foreground">{error instanceof Error ? error.message : 'Unknown error.'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="space-y-3 p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold">Business income</h2>
                <Badge variant="outline">
                  {e.business.company_type || 'Pass-through'} · {(e.business.business_income_rate_assumed * 100).toFixed(1)}% rate
                </Badge>
              </div>
              <Row label="Revenue (posted)" value={formatPayrollMoney(e.business.revenue_minor_units)} hint="Credits to income accounts" />
              <Row label="Cost of goods sold" value={formatPayrollMoney(e.business.cogs_minor_units)} hint="Debits to COGS" />
              <Row label="Gross profit" value={formatPayrollMoney(e.business.gross_profit_minor_units)} accent="success" />
              <Row label="Operating expenses" value={formatPayrollMoney(e.business.operating_expenses_minor_units)} />
              <Row label="Other expense" value={formatPayrollMoney(e.business.other_expense_minor_units)} />
              <Row label="Net income" value={formatPayrollMoney(e.business.net_income_minor_units)} accent="success" />
              <Row label="Estimated federal income tax" value={formatPayrollMoney(e.business.estimated_federal_income_tax_minor_units)} accent="warning" hint={`Applied at ${(e.business.business_income_rate_assumed * 100).toFixed(1)}%`} />
              <Row label="Estimated quarterly payment (safe harbor)" value={formatPayrollMoney(e.business.estimated_quarterly_payment_minor_units)} hint="27.5% of the annual estimate" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Receipt className="size-4 text-primary" /> Sales tax collected
              </h2>
              <Row
                label="Total collected on posted invoices"
                value={formatPayrollMoney(e.sales_tax.total_collected_minor_units)}
                accent="success"
                hint={`Across ${formatPayrollMoney(e.sales_tax.total_taxable_revenue_minor_units)} taxable revenue`}
              />
              {e.sales_tax.by_jurisdiction.length > 0 ? (
                <DataTable
                  columns={[
                    { accessorKey: 'jurisdiction', header: 'Jurisdiction' },
                    { accessorKey: 'invoice_count', header: 'Invoices' },
                    {
                      accessorKey: 'taxable_revenue_minor_units',
                      header: 'Taxable revenue',
                      cell: ({ row }: { row: { original: { taxable_revenue_minor_units: string } } }) =>
                        formatPayrollMoney(row.original.taxable_revenue_minor_units),
                    },
                    {
                      accessorKey: 'sales_tax_collected_minor_units',
                      header: 'Tax collected',
                      cell: ({ row }: { row: { original: { sales_tax_collected_minor_units: string } } }) =>
                        formatPayrollMoney(row.original.sales_tax_collected_minor_units),
                    },
                  ] as never}
                  rows={e.sales_tax.by_jurisdiction}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No posted invoices in this period. Sales tax appears here automatically once invoices land.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <IdCard className="size-4 text-primary" /> 1099-NEC candidates
              </h2>
              <p className="text-sm text-muted-foreground">
                Vendors paid ≥ ${(e.contractor_1099_candidates.threshold_minor_units / 100).toFixed(0)} this year are 1099-NEC reportable. {e.contractor_1099_candidates.candidate_count} found.
              </p>
              {e.contractor_1099_candidates.candidates.length > 0 ? (
                <DataTable
                  columns={[
                    { accessorKey: 'vendor_name', header: 'Vendor' },
                    { accessorKey: 'vendor_state', header: 'State' },
                    { accessorKey: 'bill_count', header: 'Bills' },
                    {
                      accessorKey: 'total_paid_minor_units',
                      header: 'YTD paid',
                      cell: ({ row }: { row: { original: { total_paid_minor_units: string } } }) =>
                        formatPayrollMoney(row.original.total_paid_minor_units),
                    },
                    {
                      accessorKey: 'reportable_on_1099',
                      header: 'Reportable',
                      cell: ({ row }: { row: { original: { reportable_on_1099: boolean } } }) =>
                        row.original.reportable_on_1099
                          ? <Badge variant="success">Yes</Badge>
                          : <Badge variant="outline">Below threshold</Badge>,
                    },
                  ] as never}
                  rows={e.contractor_1099_candidates.candidates}
                />
              ) : (
                <p className="text-sm text-muted-foreground">No vendors over the $600 reporting threshold yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Wallet className="size-4 text-primary" /> Payroll tax
              </h2>
              <Row label="Posted pay runs in period" value={String(e.payroll.posted_runs)} />
              <Row label="Total gross wages" value={formatPayrollMoney(e.payroll.gross_minor_units)} />
              <Row label="Employee tax withheld" value={formatPayrollMoney(e.payroll.employee_tax_minor_units)} accent="warning" />
              <Row label="Employer tax expense" value={formatPayrollMoney(e.payroll.employer_tax_minor_units)} accent="warning" />
            </CardContent>
          </Card>

          {e.filings.overdue > 0 ? (
            <Card>
              <CardContent className="space-y-2 p-5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-5 text-destructive" />
                  <h2 className="text-base font-semibold text-destructive">{e.filings.overdue} overdue filing(s)</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Tax → All Filings shows the breakdown. File ASAP to limit late-filing penalties.
                </p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardContent className="space-y-2 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <TrendingUp className="size-4 text-muted-foreground" /> How this is computed
              </h2>
              <ul className="ml-4 list-disc space-y-1 text-xs text-muted-foreground">
                {e.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  )
}
